/**
 * Türkçe (tr) — `content/en.ts`'in bir parçası (partial).
 *
 * Yazılan bir dil, çevrilen bir dil değil. Türkiye Digitopia'nın ana pazarı, ve
 * İngilizce cümlelerin birebir karşılığı hem uzun hem yabancı duruyor. Bölüm
 * sırası, iddialar ve sayılar İngilizcesiyle aynı; değişen şey nasıl söylendiği.
 *
 * **Diziler bütündür.** `Translation` bunu böyle tanımlıyor: `nav.items` ya da
 * bir sayfanın `sections` dizisi ya tamamen verilir ya hiç verilmez. Yarım bir
 * özellik listesi kısmi çeviri değil, bozuk bir sayfadır.
 *
 * **Bağlantılar `/en/…` olarak kalıyor** ve okunduğu dile `localizeHref()` ile
 * çeviriliyor (`src/i18n/href.ts`). Yolların altı dilde altı kopyası olsun ve
 * sayfa taşındığında altısı birlikte taşınsın diye değil.
 *
 * Burada yazılmamış olan: `site.name`, `site.titleSuffix` ve `footer.signOff`
 * — marka adı ve etiketler çevrilmez.
 */
import { CONTACT_FALLBACK_EMAIL } from '../lib/contact'
import type { Translation } from '@/i18n/dictionary'
import { pagesFrom } from './pageRegistry'
import { LINKS } from './pages/links'
import { toolsUiTr } from './tools/ui.tr'
import { diagnosticTr } from './tools/diagnostic.tr'
import { roiCalculatorTr } from './tools/roi-calculator.tr'
import { gutsCheckTr } from './tools/guts-check.tr'
import { ariTr } from './tools/ari.tr'
import { BAND_FORM_TR } from './bandForm'

const PAGES = pagesFrom(
  import.meta.glob<Record<string, unknown>>('./pages/tr/*.ts', { eager: true }),
)

export const tr: Translation = {
  site: {
    tagline: 'Keşfet. Hayata Geçir. Sonuç Al.',
    description:
      'Strateji danışmanlığı, dönüşüm yönetimi ve kurumsal yapay zeka çözümleri. Digitopia ajan tabanlı yapay zekayı sahaya alır; elinizde pilot değil, sonuç kalır.',
  },

  nav: {
    languageLabel: 'Dil',
    skipToContent: 'İçeriğe geç',
    menu: 'Menü',
    login: 'Impact Platform',
    cta: { label: 'İletişime Geç', href: LINKS.contact },
    // Diziler bütün olduğu için üst menü, altındaki açılır pencereleriyle
    // birlikte veriliyor: `blurb` pencerenin sol sütunu, `menu` yanındaki liste.
    items: [
      {
        label: 'Çözümler',
        href: '/en/solutions',
        blurb: 'Sonuç odaklı bir yaklaşım.',
        menu: [
          {
            label: 'Strateji Danışmanlığı',
            href: LINKS.strategy,
            description: 'Ajan tabanlı yapay zeka yolculuğunuzu kurgulayın ve önceliklendirin.',
          },
          {
            label: 'Dönüşüm Yönetimi',
            href: LINKS.transformation,
            description: 'Ajan tabanlı yapay zeka yolculuğunuzu güvenceye alın ve hızlandırın.',
          },
          {
            label: 'Kurumsal Yapay Zeka Çözümleri',
            href: LINKS.enterpriseAi,
            description: 'Ajan tabanlı yapay zeka çözümlerinizi ölçekte devreye alın.',
          },
        ],
      },
      {
        label: 'Müşteriler',
        href: LINKS.successStories,
        blurb: 'Kimlerle çalışıyoruz ve çalıştıktan sonra ne değişti.',
        menu: [
          {
            label: 'Hall of Fame',
            href: LINKS.hallOfFame,
            description: 'Dijital ve yapay zeka olgunluğunda önde giden şirketler.',
          },
          {
            label: 'Başarı Hikâyeleri',
            href: LINKS.successStories,
            description: 'Değişimi kendi anlattıkları gibi.',
          },
        ],
      },
      {
        label: 'Partnerler',
        href: '/en/partner-program',
        blurb: 'Dönüşümü bizimle birlikte, kendi müşterilerinize taşıyın.',
        menu: [
          {
            label: 'İş Ortaklığı Programı',
            href: '/en/partner-program',
            description: 'Metodolojimizi kendi pazarınıza götürün.',
          },
        ],
      },
      {
        label: 'Kaynaklar',
        href: '/en/impact-labs',
        blurb: 'Impact Labs — dijital ve yapay zeka dönüşümü üzerine yazdıklarımız.',
        menu: [
          {
            label: 'Araçlar',
            href: '/en/tools',
            description: 'Dört kısa araç, her biri üzerinde tartışabileceğiniz bir sonuçla bitiyor.',
          },
          { label: 'Blog', href: '/en/blog', description: 'Sahadan gelen haftalık yazılar.' },
          {
            label: 'Benchmark',
            href: '/en/impact-labs/benchmark',
            description: 'Sektöre, ülkeye ve büyüklüğe göre digital ve AI maturity.',
          },
          {
            label: 'Raporlar',
            href: LINKS.reports,
            description: 'Benchmark araştırmaları ve olgunluk bulguları.',
          },
          {
            label: 'E-kitaplar',
            href: '/en/impact-labs/ebooks',
            description: 'Daha uzun rehberler, ücretsiz.',
          },
          {
            label: 'Podcast’ler',
            href: '/en/impact-labs/podcasts',
            description: 'Dönüşüm liderleriyle sohbetler.',
          },
        ],
      },
      {
        label: 'Hakkımızda',
        href: LINKS.about,
        blurb: 'Neden varız, işi kim yapıyor ve bize nasıl ulaşırsınız.',
        menu: [
          { label: 'Hakkımızda', href: LINKS.about, description: 'Neden varız ve nasıl çalışırız.' },
          { label: 'Haberler', href: '/en/impact-labs/news', description: 'Digitopia’dan duyurular.' },
          {
            label: 'Etkinlikler',
            href: '/en/impact-labs/events',
            description: 'Bizi bir sonraki nerede bulacaksınız.',
          },
          { label: 'Kariyer', href: LINKS.careers, description: 'Bir Digitopian olun.' },
          { label: 'Bize ulaşın', href: LINKS.contact, description: 'Ekibimizle konuşmaya başlayın.' },
        ],
      },
    ],
  },

  // Every Turkish page module, discovered the same way `en.ts` does it. A
  // locale that has not translated a page simply has no file for it and
  // inherits the English one, which is what it did before this was a glob.
  pages: PAGES,

  /**
   * The Diagnostic's Turkish, which came WITH the tool rather than being owed:
   * Kagan built it bilingual. Only the strings are here — the option scores live
   * in `tools/diagnostic.ts` and are read positionally, so this file cannot
   * change what the tool measures.
   *
   * The ROI Calculator's Turkish was written here on 2026-08-30 — its industry,
   * country and objective names live in the dictionary now precisely so a locale
   * can rename them without touching the model.
   *
   * The Guts Check and the Agentic Readiness Index were written on 2026-08-31,
   * with the chrome they share (`tools/ui.tr.ts`) — so all four tools now speak
   * Turkish end to end. Only strings: both tools score positionally, so the
   * options stay in English's order and nothing here changes what is measured.
   */
  tools: {
    ...toolsUiTr,
    diagnostic: diagnosticTr,
    roiCalculator: roiCalculatorTr,
    gutsCheck: gutsCheckTr,
    ari: ariTr,
  },

  contactBand: {
    heading: 'İletişime geçelim!',
    sub: 'Nereye gitmek istediğinizi anlatın, en kısa yolu birlikte kuralım.',
    form: BAND_FORM_TR,
    cta: { label: 'İletişime Geç', href: LINKS.contact },
    photoAlt: 'Digitopia gradient ribbonuyla çerçevelenmiş rengârenk kaktüsler',
  },

  footer: {
    newsletter: {
      heading: 'Aylık bülten',
      sub: 'Ayın ikinci salısı tek bir e-posta: dijital ve AI dönüşümünde ne görüyoruz, yerinizde olsak ne yapardık.',
      placeholder: 'siz@sirket.com',
      submit: 'Abone ol',
      sent: 'E-postanızı kontrol edin — onay bağlantısını gönderdik.',
      invalid: 'Bu e-posta adresi doğru görünmüyor.',
      failed: 'Bizim tarafta bir şeyler ters gitti. Birazdan tekrar deneyin.',
      checking: 'İnsan olduğunuzu doğrulayan kontrol tamamlanıyor…',
      blocked: `Güvenlik doğrulaması tarayıcınızda yüklenmedi. Sayfayı yenileyin ya da bu sitede reklam engelleyiciyi kapatın — veya bize ${CONTACT_FALLBACK_EMAIL} adresinden yazın.`,
      note: 'Önce bir onay bağlantısı gönderiyoruz; her sayıda tek tıkla çıkabilirsiniz.',
    },
    tagline: 'Keşfet. Hayata Geçir. Sonuç Al.',
    rights: 'Tüm hakları saklıdır.',
    columns: [
      {
        title: 'Çözümler',
        links: [
          { label: 'Strateji Danışmanlığı', href: LINKS.strategy },
          { label: 'Dönüşüm Yönetimi', href: LINKS.transformation },
          { label: 'Kurumsal Yapay Zeka Çözümleri', href: LINKS.enterpriseAi },
        ],
      },
      {
        title: 'Müşteriler',
        links: [
          { label: 'Hall of Fame', href: LINKS.hallOfFame },
          { label: 'Başarı Hikâyeleri', href: LINKS.successStories },
        ],
      },
      {
        title: 'Partnerler',
        links: [
          { label: 'İş Ortaklığı Programı', href: '/en/partner-program' },
        ],
      },
      {
        title: 'Kaynaklar',
        links: [
          { label: 'Blog', href: '/en/blog' },
          { label: 'Bülten', href: '/en/newsletter' },
          { label: 'Benchmark', href: '/en/impact-labs/benchmark' },
          { label: 'Raporlar', href: LINKS.reports },
          { label: 'E-kitaplar', href: '/en/impact-labs/ebooks' },
          { label: 'Haberler', href: '/en/impact-labs/news' },
          { label: 'Podcast\'ler', href: '/en/impact-labs/podcasts' },
          { label: 'Etkinlikler', href: '/en/impact-labs/events' },
        ],
      },
      {
        title: 'Kurumsal',
        links: [
          { label: 'Hakkımızda', href: LINKS.about },
          { label: 'Kariyer', href: LINKS.careers },
          { label: 'Bize ulaşın', href: LINKS.contact },
        ],
      },
    ],
    offices: 'New York. Londra. İstanbul.',
    followLabel: 'Bizi takip edin',
    legal: [
      { label: 'Gizlilik Politikası', href: '/en/privacy-policy' },
      { label: 'Kullanım Koşulları', href: '/en/terms-of-service' },
      { label: 'Çerez Bildirimi', href: '/en/cookie-and-data-privacy' },
    ],
  },

  preview: {
    badge: 'Önizleme',
    notice: 'Bu, digitopia.ai sitesinin onaylanmamış bir önizlemesidir. İçerik taslaktır.',
    checkLabel: 'KONTROL',
  },

  ui: {
    previous: 'Önceki',
    next: 'Sonraki',
  },
}
